import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { handleSessionExpired } from '../utils/sessionExpired'
import { clearAuthToken, getAuthToken } from '../utils/authStorage'
import { authService } from '../services/authService'
import LoadingSpinner from './LoadingSpinner'

function isAuthHydrated(): boolean {
  return useAuthStore.getState()._hasHydrated || useAuthStore.persist.hasHydrated()
}

interface LoginRedirectProps {
  children: React.ReactNode
}

/** Which role this login page is for (null = home student auth). */
function portalRoleForPath(
  pathname: string,
): 'admin' | 'tutor' | 'school' | 'student' | 'affiliate' | null {
  if (pathname.startsWith('/admin')) return 'admin'
  if (pathname.startsWith('/tutor') || pathname.startsWith('/instructor')) return 'tutor'
  if (pathname.startsWith('/school')) return 'school'
  if (pathname.startsWith('/affiliate')) return 'affiliate'
  return null
}

const LoginRedirect: React.FC<LoginRedirectProps> = ({ children }) => {
  const { isLoggedIn, user, _hasHydrated, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [hydrationReady, setHydrationReady] = useState(isAuthHydrated)
  const [clearedMismatch, setClearedMismatch] = useState(false)

  useEffect(() => {
    if (isAuthHydrated()) {
      setHydrationReady(true)
      return
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      useAuthStore.getState().setHasHydrated(true)
      setHydrationReady(true)
    })
    const timeout = window.setTimeout(() => setHydrationReady(true), 1200)
    return () => {
      unsub()
      window.clearTimeout(timeout)
    }
  }, [_hasHydrated])

  useEffect(() => {
    if (!hydrationReady) return

    const portalRole = portalRoleForPath(location.pathname)

    // Already logged in as the correct role for this portal → go to dashboard.
    if (isLoggedIn && user && portalRole && user.role === portalRole) {
      let redirectPath = '/'
      switch (user.role) {
        case 'admin':
          redirectPath = '/admin/dashboard'
          break
        case 'tutor':
          redirectPath = '/tutor/profile'
          break
        case 'school':
          redirectPath = '/school/dashboard'
          break
        case 'student':
          redirectPath = '/messages'
          break
        case 'affiliate':
          redirectPath = '/affiliate'
          break
        default:
          redirectPath = '/'
      }
      navigate(redirectPath, { replace: true })
      return
    }

    // Wrong role on a portal login (e.g. student session on /admin/login):
    // clear session so the admin form is usable.
    if (
      isLoggedIn &&
      user &&
      portalRole &&
      user.role !== portalRole &&
      !clearedMismatch
    ) {
      setClearedMismatch(true)
      clearAuthToken()
      logout()
      void authService.logout().catch(() => undefined)
    }
  }, [
    isLoggedIn,
    user,
    navigate,
    location.pathname,
    hydrationReady,
    logout,
    clearedMismatch,
  ])

  if (!hydrationReady) {
    return <LoadingSpinner />
  }

  const hasToken = Boolean(getAuthToken())
  if (isLoggedIn && !hasToken) {
    handleSessionExpired(location.pathname)
    return <LoadingSpinner />
  }

  const portalRole = portalRoleForPath(location.pathname)
  if (isLoggedIn && user && portalRole && user.role === portalRole) {
    return <LoadingSpinner />
  }

  // Wrong-role session being cleared, or not logged in → show login form.
  return <>{children}</>
}

export default LoginRedirect
